import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { usePolarisRecaptcha } from "../hooks/usePolarisRecaptcha";
import { buildApiUrl } from "../utils/apiConfig";
import { RecaptchaDisclosure } from "./RecaptchaDisclosure";
import "../styles/contact-page.css";
import "../styles/family-plan-inquiry-modal.css";
import { apiClient } from "../utils/apiClient";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_MESSAGE_LENGTH = 20;
const MESSAGE_MAX = 2000;

const RECAPTCHA_ACTION = "contact_family";
const API_ENDPOINT = "api/contact/family-request";

interface FamilyPlanInquiryModalProps {
  open: boolean;
  onClose: () => void;
}

interface FieldErrors {
  email?: string;
  message?: string;
}

export function FamilyPlanInquiryModal({ open, onClose }: FamilyPlanInquiryModalProps) {
  const headingId = useId();
  const emailId = useId();
  const messageId = useId();
  const { executeRecaptcha } = usePolarisRecaptcha();

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setMessage("");
    setErrors({});
    setServerError("");
    setSubmitting(false);
    setSubmitted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      errs.email = "Email address is required.";
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      errs.email = "Please enter a valid email address.";
    }
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      errs.message = "Message is required.";
    } else if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
      errs.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters.`;
    }
    return errs;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError("");
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);
      const res = await apiClient(buildApiUrl(API_ENDPOINT), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
          recaptchaToken,
        }),
      });
      if (!res.ok) {
        setServerError(
          res.status === 400 || res.status === 422
            ? "Please enter a valid email and message, then try again."
            : "We couldn't send your inquiry right now. Please try again in a little while."
        );
        return;
      }
      setSubmitted(true);
    } catch (err) {
      const recaptchaFailed = err instanceof Error && err.message === "recaptcha-not-ready";
      setServerError(
        recaptchaFailed
          ? "We couldn't verify this request. Please refresh the page and try again."
          : "We couldn't send your inquiry right now. Please try again in a little while."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className="family-inquiry-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="contact-form-card family-inquiry-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="family-inquiry-modal"
      >
        <button
          type="button"
          className="family-inquiry-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="family-inquiry-close"
        >
          ×
        </button>

        {submitted ? (
          <div className="contact-success" data-testid="family-inquiry-success">
            <div className="contact-success-check">&#10003;</div>
            <h3 className="contact-success-heading">Request Sent</h3>
            <p className="contact-success-text">
              Thank you for your interest! We&apos;ve sent a confirmation to your email and will be
              in touch shortly.
            </p>
            <button
              type="button"
              className="contact-form-submit-btn"
              onClick={onClose}
              data-testid="family-inquiry-done"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <h2 id={headingId} className="contact-form-heading">
              Get In Touch
            </h2>
            {serverError && (
              <div className="contact-form-server-error" role="alert">
                {serverError}
              </div>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="contact-form-group">
                <label className="contact-form-label" htmlFor={emailId}>
                  Your Email
                </label>
                <div className="contact-form-input-wrapper">
                  <input
                    id={emailId}
                    type="email"
                    className={`contact-form-input${errors.email ? " contact-form-input--error" : ""}`}
                    placeholder="your email..."
                    value={email}
                    data-testid="family-inquiry-email"
                    aria-required="true"
                    aria-invalid={errors.email ? "true" : undefined}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                  />
                  <span className="contact-form-field-icon" aria-hidden="true">
                    &#9992;
                  </span>
                </div>
                {errors.email && <p className="contact-form-error">{errors.email}</p>}
              </div>
              <div className="contact-form-group">
                <label className="contact-form-label" htmlFor={messageId}>
                  Message
                </label>
                <div className="contact-form-textarea-wrapper">
                  <textarea
                    id={messageId}
                    className={`contact-form-textarea${
                      errors.message ? " contact-form-textarea--error" : ""
                    }`}
                    placeholder="write message.."
                    value={message}
                    maxLength={MESSAGE_MAX}
                    data-testid="family-inquiry-message"
                    aria-required="true"
                    aria-invalid={errors.message ? "true" : undefined}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      setErrors((prev) => ({ ...prev, message: undefined }));
                    }}
                  />
                  <span className="contact-form-textarea-icon" aria-hidden="true">
                    &#9993;
                  </span>
                </div>
                {errors.message && <p className="contact-form-error">{errors.message}</p>}
              </div>
              <button
                type="submit"
                className="contact-form-submit-btn"
                disabled={submitting}
                data-testid="family-inquiry-submit"
              >
                {submitting ? "Sending..." : "Discover More"}
                {!submitting && <span className="contact-form-submit-arrow">&rarr;</span>}
              </button>
              <RecaptchaDisclosure className="recaptcha-disclosure--on-dark" />
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default FamilyPlanInquiryModal;
